import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaChartBar, FaExchangeAlt, FaTrophy, FaChartLine, FaTimes } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getFriendlyErrorMessage } from "../utils/friendlyError";

export default function ComparativeAnalyticsPage() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_SERVER_URL || 'http://localhost:8080';
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('device-comparison');
  
  // Device Comparison State
  const [deviceComp, setDeviceComp] = useState({
    device1: '',
    device2: '',
    metric: 'upload',
    timeRange: '7 days'
  });
  const [deviceResult, setDeviceResult] = useState(null);
  const [deviceLoading, setDeviceLoading] = useState(false);
  
  // Yard Comparison State
  const [yardComp, setYardComp] = useState({
    yard1: '',
    yard2: '',
    metric: 'upload',
    timeRange: '7 days',
    activeDevicesOnly: false,
    activeDeviceWindow: 4
  });
  const [yardCompResult, setYardCompResult] = useState(null);
  const [yardCompLoading, setYardCompLoading] = useState(false);
  
  // Yard Ranking State
  const [yardRanking, setYardRanking] = useState({
    metric: 'upload',
    timeRange: '30 days',
    limit: 10,
    minDevices: 1,
    order: 'top' // 'top' for best performing, 'bottom' for worst performing
  });
  const [yardResult, setYardResult] = useState(null);
  const [yardResultMetric, setYardResultMetric] = useState(null);
  const [yardLoading, setYardLoading] = useState(false);
  
  // Carrier Trends State
  const [carrierTrends, setCarrierTrends] = useState({
    metric: 'upload',
    timeRange: '30 days',
    interval: 'day',
    yard_numbers: [] // [] = all yards, ["43","207"] = per-yard (side-by-side charts)
  });
  const [carrierYardInput, setCarrierYardInput] = useState(''); // current input for "Add yard"
  const [carrierResult, setCarrierResult] = useState(null); // Option B: [{ yard_number, carriers: [{ carrier, data }] }]
  const [carrierResultMetric, setCarrierResultMetric] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(false);
  /** Per-yard carrier selection for the inline graph: { [yard_number]: ['T-Mobile','AT&T'] }. Empty/undefined = show all carriers for that yard. */
  const [carrierSelectionByYard, setCarrierSelectionByYard] = useState({});
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [graphView, setGraphView] = useState(null);
  const [graphCarrier, setGraphCarrier] = useState(null);
  const [graphYardNumber, setGraphYardNumber] = useState(null);
  
  // Compare Devices
  const handleCompareDevices = async () => {
    if (!deviceComp.device1 || !deviceComp.device2) {
      alert('Please enter both device IDs');
      return;
    }
    
    setDeviceLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/analytics/compare-devices`, deviceComp);
      setDeviceResult({ ...response.data, submittedMetric: deviceComp.metric });
    } catch (err) {
      console.error('Error comparing devices:', err);
      alert(getFriendlyErrorMessage(err.response?.data?.error, 'Could not compare devices right now.'));
    } finally {
      setDeviceLoading(false);
    }
  };
  
  // Compare Yards
  const handleCompareYards = async () => {
    if (!yardComp.yard1 || !yardComp.yard2) {
      alert('Please enter both yard numbers');
      return;
    }
    
    setYardCompLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/analytics/compare-yards`, yardComp);
      setYardCompResult({ ...response.data, submittedMetric: yardComp.metric });
    } catch (err) {
      console.error('Error comparing yards:', err);
      alert(getFriendlyErrorMessage(err.response?.data?.error, 'Could not compare yards right now.'));
    } finally {
      setYardCompLoading(false);
    }
  };
  
  // Rank Yards
  const handleRankYards = async () => {
    setYardLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/analytics/rank-yards`, yardRanking);
      setYardResult(response.data);
      setYardResultMetric(yardRanking.metric);
    } catch (err) {
      console.error('Error ranking yards:', err);
      alert(getFriendlyErrorMessage(err.response?.data?.error, 'Could not load yard rankings right now.'));
    } finally {
      setYardLoading(false);
    }
  };
  
  // Get Carrier Trends (sends yard_numbers: [] for all yards, or ["43","207"] for per-yard)
  const handleCarrierTrends = async () => {
    setCarrierLoading(true);
    setSelectedCarriers([]);
    setCarrierSelectionByYard({});
    setGraphView(null);
    setGraphCarrier(null);
    setGraphYardNumber(null);
    try {
      const response = await axios.post(`${API_BASE}/maiApi/analytics/carrier-trends`, {
        ...carrierTrends,
        yard_numbers: carrierTrends.yard_numbers || []
      });
      setCarrierResult(response.data);
      setCarrierResultMetric(carrierTrends.metric);
    } catch (err) {
      console.error('Error getting carrier trends:', err);
      alert(getFriendlyErrorMessage(err.response?.data?.error, 'Could not load carrier trends right now.'));
    } finally {
      setCarrierLoading(false);
    }
  };

  // Effective carriers to show on a yard's graph: selection for that yard, or all if none set
  const getEffectiveCarriersForYard = (yardNumber, allCarrierNames) => {
    const selected = carrierSelectionByYard[yardNumber];
    if (!selected || selected.length === 0) return allCarrierNames || [];
    return selected.filter((c) => (allCarrierNames || []).includes(c));
  };

  // Toggle one carrier on/off for a yard's graph
  const toggleCarrierForYard = (yardNumber, carrierName) => {
    const all = (carrierResult?.find((y) => y.yard_number === yardNumber)?.carriers || []).map((c) => c.carrier);
    const current = getEffectiveCarriersForYard(yardNumber, all);
    const next = current.includes(carrierName)
      ? current.filter((c) => c !== carrierName)
      : [...current, carrierName];
    setCarrierSelectionByYard((prev) => ({ ...prev, [yardNumber]: next }));
  };

  // Select all / clear all carriers for a yard
  const setAllCarriersForYard = (yardNumber, selected) => {
    const all = (carrierResult?.find((y) => y.yard_number === yardNumber)?.carriers || []).map((c) => c.carrier);
    setCarrierSelectionByYard((prev) => ({ ...prev, [yardNumber]: selected ? all : [] }));
  };

  // Add/remove yards for carrier trends
  const addCarrierTrendYard = () => {
    const y = String(carrierYardInput).trim();
    if (!y) return;
    if ((carrierTrends.yard_numbers || []).includes(y)) return;
    setCarrierTrends((prev) => ({
      ...prev,
      yard_numbers: [...(prev.yard_numbers || []), y]
    }));
    setCarrierYardInput('');
  };
  const removeCarrierTrendYard = (yard) => {
    setCarrierTrends((prev) => ({
      ...prev,
      yard_numbers: (prev.yard_numbers || []).filter((y) => y !== yard)
    }));
  };

  // Toggle carrier selection
  const toggleCarrierSelection = (carrierName) => {
    setSelectedCarriers(prev => 
      prev.includes(carrierName)
        ? prev.filter(c => c !== carrierName)
        : [...prev, carrierName]
    );
  };

  // Format data for individual graph (single carrier)
  const formatIndividualGraphData = (carrierData) => {
    return carrierData.data.map(point => ({
      time: new Date(point.time_period).toLocaleString(),
      value: parseFloat(point.avg_metric) || 0,
      devices: point.device_count
    }));
  };

  // Build chart data for one yard block. Optional carriersToShow = array of carrier names to include (default: all).
  const formatGraphDataForYardBlock = (yardBlock, carriersToShow = null) => {
    if (!yardBlock || !yardBlock.carriers || yardBlock.carriers.length === 0) return { data: [], carrierNames: [] };
    const carriers =
      carriersToShow && carriersToShow.length > 0
        ? yardBlock.carriers.filter((c) => carriersToShow.includes(c.carrier))
        : yardBlock.carriers;
    if (carriers.length === 0) return { data: [], carrierNames: [] };
    const allTimePeriods = new Set();
    carriers.forEach(({ data }) => {
      (data || []).forEach((point) => {
        const timeKey = point.time_period ? String(point.time_period) : null;
        if (timeKey) allTimePeriods.add(timeKey);
      });
    });
    const carrierNames = carriers.map((c) => c.carrier);
    const data = Array.from(allTimePeriods)
      .map((timePeriodStr) => {
        const dataPoint = {
          time: new Date(timePeriodStr).toLocaleString(),
          timePeriod: timePeriodStr
        };
        carriers.forEach(({ carrier, data: carrierData }) => {
          const point = (carrierData || []).find((p) => String(p.time_period) === timePeriodStr);
          dataPoint[carrier] = point ? parseFloat(point.avg_metric) || 0 : null;
        });
        return dataPoint;
      })
      .sort((a, b) => {
        try {
          return new Date(a.timePeriod) - new Date(b.timePeriod);
        } catch (e) {
          return 0;
        }
      });
    return { data, carrierNames };
  };

  // Format data for combined graph (modal): from one yard block + selectedCarriers
  const formatCombinedGraphData = (yardBlock) => {
    if (!yardBlock || !yardBlock.carriers || selectedCarriers.length === 0) return [];
    const allTimePeriods = new Set();
    selectedCarriers.forEach((carrierName) => {
      const carrier = yardBlock.carriers.find((c) => c.carrier === carrierName);
      if (carrier && carrier.data) {
        carrier.data.forEach((point) => {
          const timeKey = point.time_period ? String(point.time_period) : null;
          if (timeKey) allTimePeriods.add(timeKey);
        });
      }
    });
    return Array.from(allTimePeriods)
      .map((timePeriodStr) => {
        const dataPoint = {
          time: new Date(timePeriodStr).toLocaleString(),
          timePeriod: timePeriodStr
        };
        selectedCarriers.forEach((carrierName) => {
          const carrier = yardBlock.carriers.find((c) => c.carrier === carrierName);
          dataPoint[carrierName] =
            carrier && carrier.data
              ? (() => {
                  const point = carrier.data.find((p) => String(p.time_period) === timePeriodStr);
                  return point ? parseFloat(point.avg_metric) || 0 : null;
                })()
              : null;
        });
        return dataPoint;
      })
      .sort((a, b) => {
        try {
          return new Date(a.timePeriod) - new Date(b.timePeriod);
        } catch (e) {
          return 0;
        }
      });
  };

  const getMetricUnit = (metric) => {
    if (metric === 'upload' || metric === 'download') return 'Mbps';
    if (metric === 'ping') return 'ms';
    if (metric === 'signal') return 'dBm';
    if (metric === 'battery') return '%';
    return '';
  };

  const getMetricLabel = (metric) => {
    if (metric === 'upload') return 'Upload Speed';
    if (metric === 'download') return 'Download Speed';
    if (metric === 'ping') return 'Ping/Latency';
    if (metric === 'signal') return 'Signal Strength';
    if (metric === 'battery') return 'Battery';
    return metric || '';
  };

  const getShortMetricLabel = (metric) => {
    if (metric === 'upload') return 'Upload';
    if (metric === 'download') return 'Download';
    if (metric === 'ping') return 'Ping';
    if (metric === 'signal') return 'Signal';
    if (metric === 'battery') return 'Battery';
    return metric || '';
  };
  
  return (
    <div className="w-full p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Comparative Analytics</h1>
        <p className="text-gray-600">Compare devices, rank yards, track trends, and analyze performance changes</p>
      </div>
      
      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveTab('device-comparison')}
            className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
              activeTab === 'device-comparison'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaExchangeAlt className="inline mr-2" />
            Compare Devices
          </button>
          <button
            onClick={() => setActiveTab('yard-comparison')}
            className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
              activeTab === 'yard-comparison'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaChartBar className="inline mr-2" />
            Compare Yards
          </button>
          <button
            onClick={() => setActiveTab('yard-ranking')}
            className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
              activeTab === 'yard-ranking'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaTrophy className="inline mr-2" />
            Rank Yards
          </button>
          <button
            onClick={() => setActiveTab('carrier-trends')}
            className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
              activeTab === 'carrier-trends'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FaChartLine className="inline mr-2" />
            Carrier Trends
          </button>
        </div>
      </div>
      
      {/* Device Comparison Tab */}
      {activeTab === 'device-comparison' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">Compare Two Devices</h2>
          <p className="text-gray-600 mb-6">Compare performance metrics between two devices over a time period</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Device 1 Android ID</label>
              <input
                type="text"
                value={deviceComp.device1}
                onChange={(e) => setDeviceComp({ ...deviceComp, device1: e.target.value })}
                placeholder="e.g., 52a75fd7a62b2f0e"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Device 2 Android ID</label>
              <input
                type="text"
                value={deviceComp.device2}
                onChange={(e) => setDeviceComp({ ...deviceComp, device2: e.target.value })}
                placeholder="e.g., 1a6e7e7633502386"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <select
                value={deviceComp.metric}
                onChange={(e) => setDeviceComp({ ...deviceComp, metric: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="upload">Upload Speed</option>
                <option value="download">Download Speed</option>
                <option value="ping">Ping/Latency</option>
                <option value="signal">Signal Strength</option>
                <option value="battery">Battery</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <select
                value={deviceComp.timeRange}
                onChange={(e) => setDeviceComp({ ...deviceComp, timeRange: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="24 hours">Last 24 Hours</option>
                <option value="7 days">Last 7 Days</option>
                <option value="30 days">Last 30 Days</option>
                <option value="90 days">Last 90 Days</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={handleCompareDevices}
            disabled={deviceLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {deviceLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Comparing...</span>
              </>
            ) : (
              <>
                <FaChartBar />
                <span>Compare Devices</span>
              </>
            )}
          </button>
          
          {/* Results */}
          {deviceResult && (
            <div className="mt-6 space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold mb-4">Comparison Results</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Device 1 */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Device 1</h4>
                    <p className="text-xs text-gray-500 font-mono mb-3">{deviceResult.device1.android_id}</p>
                    {deviceResult.device1.stats ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-bold text-gray-800">
                            {deviceResult.device1.stats.avg.toFixed(2)} {getMetricUnit(deviceResult.submittedMetric)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Range:</span>
                          <span>{deviceResult.device1.stats.min.toFixed(2)} - {deviceResult.device1.stats.max.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Data Points:</span>
                          <span>{deviceResult.device1.stats.count}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                  
                  {/* Device 2 */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Device 2</h4>
                    <p className="text-xs text-gray-500 font-mono mb-3">{deviceResult.device2.android_id}</p>
                    {deviceResult.device2.stats ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-bold text-gray-800">
                            {deviceResult.device2.stats.avg.toFixed(2)} {getMetricUnit(deviceResult.submittedMetric)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Range:</span>
                          <span>{deviceResult.device2.stats.min.toFixed(2)} - {deviceResult.device2.stats.max.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Data Points:</span>
                          <span>{deviceResult.device2.stats.count}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                </div>
                
                {/* Comparison Summary */}
                {deviceResult.comparison && deviceResult.comparison.winner && (
                  <div className={`mt-4 p-4 rounded-lg border-2 ${
                    deviceResult.comparison.winner === deviceComp.device1 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-green-500 bg-green-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">
                          Winner: Device {deviceResult.comparison.winner === deviceComp.device1 ? '1' : '2'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Device 1: {deviceResult.device1.android_id} ({deviceResult.device1.carrier || 'Unknown'})<br/>
                          Device 2: {deviceResult.device2.android_id} ({deviceResult.device2.carrier || 'Unknown'})
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Difference: {Math.abs(deviceResult.comparison.difference).toFixed(2)} {getMetricUnit(deviceResult.submittedMetric)}
                          {deviceResult.comparison.percentDiff && ` (${Math.abs(parseFloat(deviceResult.comparison.percentDiff)).toFixed(1)}%)`}
                        </p>
                      </div>
                      <div className="text-2xl">
                        {deviceResult.comparison.winner === deviceComp.device1 ? '🏆' : '🏆'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Yard Comparison Tab */}
      {activeTab === 'yard-comparison' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">Compare Two Yards</h2>
          <p className="text-gray-600 mb-6">Compare performance metrics between two yards over a time period</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Yard 1 Number</label>
              <input
                type="text"
                value={yardComp.yard1}
                onChange={(e) => setYardComp({ ...yardComp, yard1: e.target.value })}
                placeholder="e.g., 207"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Yard 2 Number</label>
              <input
                type="text"
                value={yardComp.yard2}
                onChange={(e) => setYardComp({ ...yardComp, yard2: e.target.value })}
                placeholder="e.g., 43"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <select
                value={yardComp.metric}
                onChange={(e) => setYardComp({ ...yardComp, metric: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="upload">Upload Speed</option>
                <option value="download">Download Speed</option>
                <option value="ping">Ping/Latency</option>
                <option value="signal">Signal Strength</option>
                <option value="battery">Battery</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <select
                value={yardComp.timeRange}
                onChange={(e) => setYardComp({ ...yardComp, timeRange: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="24 hours">Last 24 Hours</option>
                <option value="7 days">Last 7 Days</option>
                <option value="30 days">Last 30 Days</option>
                <option value="90 days">Last 90 Days</option>
              </select>
            </div>
          </div>
          
          {/* Active Devices Filter */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={yardComp.activeDevicesOnly}
                  onChange={(e) => setYardComp({ ...yardComp, activeDevicesOnly: e.target.checked })}
                  className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Only include active devices
                </span>
              </label>
              {yardComp.activeDevicesOnly && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Active within last:</label>
                  <select
                    value={yardComp.activeDeviceWindow}
                    onChange={(e) => setYardComp({ ...yardComp, activeDeviceWindow: parseInt(e.target.value) })}
                    className="text-sm p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="2">2 hours</option>
                    <option value="4">4 hours</option>
                    <option value="6">6 hours</option>
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                  </select>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-2 ml-6">
              {yardComp.activeDevicesOnly 
                ? `Only devices that sent data within the last ${yardComp.activeDeviceWindow} hours will be included. This ensures fair comparison of active devices only.`
                : 'Include all devices that sent data in the time range (may include offline devices).'}
            </p>
          </div>
          
          <button
            onClick={handleCompareYards}
            disabled={yardCompLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {yardCompLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Comparing...</span>
              </>
            ) : (
              <>
                <FaChartBar />
                <span>Compare Yards</span>
              </>
            )}
          </button>
          
          {/* Results */}
          {yardCompResult && (
            <div className="mt-6 space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold mb-4">Comparison Results</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Yard 1 */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Yard {yardCompResult.yard1.yard_number}</h4>
                    {yardCompResult.yard1.yard_name && (
                      <p className="text-xs text-gray-500 mb-1">{yardCompResult.yard1.yard_name}</p>
                    )}
                    {yardCompResult.yard1.yard_address && (
                      <p className="text-xs text-gray-400 mb-3">{yardCompResult.yard1.yard_address}</p>
                    )}
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-1">Carriers:</p>
                      <div className="flex flex-wrap gap-1">
                        {yardCompResult.yard1.carriers && yardCompResult.yard1.carriers.length > 0 ? (
                          yardCompResult.yard1.carriers.map((carrier, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {carrier}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No carrier data</span>
                        )}
                      </div>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Devices: <span className="font-semibold">{yardCompResult.yard1.device_count || 0}</span></p>
                    </div>
                    {yardCompResult.yard1.stats ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-bold text-gray-800">
                            {yardCompResult.yard1.stats.avg.toFixed(2)} {getMetricUnit(yardCompResult.submittedMetric)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Range:</span>
                          <span>{yardCompResult.yard1.stats.min.toFixed(2)} - {yardCompResult.yard1.stats.max.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Data Points:</span>
                          <span>{yardCompResult.yard1.stats.count}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                  
                  {/* Yard 2 */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Yard {yardCompResult.yard2.yard_number}</h4>
                    {yardCompResult.yard2.yard_name && (
                      <p className="text-xs text-gray-500 mb-1">{yardCompResult.yard2.yard_name}</p>
                    )}
                    {yardCompResult.yard2.yard_address && (
                      <p className="text-xs text-gray-400 mb-3">{yardCompResult.yard2.yard_address}</p>
                    )}
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-1">Carriers:</p>
                      <div className="flex flex-wrap gap-1">
                        {yardCompResult.yard2.carriers && yardCompResult.yard2.carriers.length > 0 ? (
                          yardCompResult.yard2.carriers.map((carrier, idx) => (
                            <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {carrier}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No carrier data</span>
                        )}
                      </div>
                    </div>
                    <div className="mb-2">
                      <p className="text-xs text-gray-600">Devices: <span className="font-semibold">{yardCompResult.yard2.device_count || 0}</span></p>
                    </div>
                    {yardCompResult.yard2.stats ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Average:</span>
                          <span className="font-bold text-gray-800">
                            {yardCompResult.yard2.stats.avg.toFixed(2)} {getMetricUnit(yardCompResult.submittedMetric)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Range:</span>
                          <span>{yardCompResult.yard2.stats.min.toFixed(2)} - {yardCompResult.yard2.stats.max.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Data Points:</span>
                          <span>{yardCompResult.yard2.stats.count}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                </div>
                
                {/* Comparison Summary */}
                {yardCompResult.comparison && yardCompResult.comparison.winner && (
                  <div className={`mt-4 p-4 rounded-lg border-2 border-green-500 bg-green-50`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">
                          Winner: Yard {yardCompResult.comparison.winner}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Yard {yardCompResult.yard1.yard_number}: {yardCompResult.yard1.carriers && yardCompResult.yard1.carriers.length > 0 ? yardCompResult.yard1.carriers.join(', ') : 'No carriers'}<br/>
                          Yard {yardCompResult.yard2.yard_number}: {yardCompResult.yard2.carriers && yardCompResult.yard2.carriers.length > 0 ? yardCompResult.yard2.carriers.join(', ') : 'No carriers'}
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Difference: {Math.abs(yardCompResult.comparison.difference).toFixed(2)} {getMetricUnit(yardCompResult.submittedMetric)}
                          {yardCompResult.comparison.percentDiff && ` (${Math.abs(parseFloat(yardCompResult.comparison.percentDiff)).toFixed(1)}%)`}
                        </p>
                      </div>
                      <div className="text-2xl">
                        🏆
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Yard Ranking Tab */}
      {activeTab === 'yard-ranking' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">Rank Yards by Performance</h2>
          <p className="text-gray-600 mb-6">See which yards have the best or worst performance metrics - identify top performers to learn from, or bottom performers that need attention</p>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <select
                value={yardRanking.metric}
                onChange={(e) => setYardRanking({ ...yardRanking, metric: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="upload">Upload Speed</option>
                <option value="download">Download Speed</option>
                <option value="ping">Ping/Latency</option>
                <option value="signal">Signal Strength</option>
                <option value="battery">Battery</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <select
                value={yardRanking.timeRange}
                onChange={(e) => setYardRanking({ ...yardRanking, timeRange: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="24 hours">Last 24 Hours</option>
                <option value="7 days">Last 7 Days</option>
                <option value="30 days">Last 30 Days</option>
                <option value="90 days">Last 90 Days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
              <select
                value={yardRanking.order}
                onChange={(e) => setYardRanking({ ...yardRanking, order: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="top">Top N Yards</option>
                <option value="bottom">Bottom N Yards</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Devices</label>
              <input
                type="number"
                value={yardRanking.minDevices}
                onChange={(e) => setYardRanking({ ...yardRanking, minDevices: parseInt(e.target.value) || 1 })}
                min="1"
                max="100"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                title="Only include yards with at least this many devices"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{yardRanking.order === 'top' ? 'Top' : 'Bottom'} N Yards</label>
              <input
                type="number"
                value={yardRanking.limit}
                onChange={(e) => setYardRanking({ ...yardRanking, limit: parseInt(e.target.value) || 10 })}
                min="1"
                max="50"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <button
            onClick={handleRankYards}
            disabled={yardLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {yardLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Ranking...</span>
              </>
            ) : (
              <>
                <FaTrophy />
                <span>Rank Yards</span>
              </>
            )}
          </button>
          
          {/* Results */}
          {yardResult && yardResult.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">
                {yardRanking.order === 'top' ? 'Top' : 'Bottom'} {yardResult.length} Yard Rankings
                {yardRanking.order === 'bottom' && <span className="text-sm text-red-600 ml-2">(Yards needing attention)</span>}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 rounded-lg">
                  <thead className={`bg-gradient-to-r ${yardRanking.order === 'top' ? 'from-blue-600 to-blue-700' : 'from-red-600 to-red-700'} text-white`}>
                    <tr>
                      <th className="p-3 border text-left">Rank</th>
                      <th className="p-3 border text-left">Yard Number</th>
                      <th className="p-3 border text-left">Location</th>
                      <th className="p-3 border text-left">Avg {getShortMetricLabel(yardResultMetric)}</th>
                      <th className="p-3 border text-left">Range</th>
                      <th className="p-3 border text-left">Devices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yardResult.map((yard, idx) => (
                      <tr key={idx} className={`border-t ${
                        yardRanking.order === 'top' 
                          ? (idx < 3 ? 'bg-yellow-50' : '') 
                          : (idx < 3 ? 'bg-red-50' : '')
                      }`}>
                        <td className="p-3 border text-center">
                          {yardRanking.order === 'top' ? (
                            <>
                              {idx === 0 && <span className="text-2xl">🥇</span>}
                              {idx === 1 && <span className="text-2xl">🥈</span>}
                              {idx === 2 && <span className="text-2xl">🥉</span>}
                              {idx >= 3 && <span className="font-semibold">{yard.rank}</span>}
                            </>
                          ) : (
                            <>
                              {idx === 0 && <span className="text-2xl">⚠️</span>}
                              {idx === 1 && <span className="text-2xl">⚠️</span>}
                              {idx === 2 && <span className="text-2xl">⚠️</span>}
                              {idx >= 3 && <span className="font-semibold text-red-600">{yard.rank}</span>}
                            </>
                          )}
                        </td>
                        <td className="p-3 border">
                          <button
                            onClick={() => {
                              const yardNumber = yard.yard_number;
                              const url = `/device-info-compact?yard=${yardNumber}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            }}
                            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            title="Click to view devices in Device Info Compact page (opens in new tab)"
                          >
                            Yard {yard.yard_number}
                          </button>
                        </td>
                        <td className="p-3 border">{yard.yard_name || 'Unknown'}</td>
                        <td className={`p-3 border font-bold ${
                          yardRanking.order === 'top' 
                            ? (idx < 3 ? 'text-green-700' : 'text-gray-800')
                            : (idx < 3 ? 'text-red-700' : 'text-gray-800')
                        }`}>
                          {yard.avg_metric.toFixed(2)} {getMetricUnit(yardResultMetric)}
                        </td>
                        <td className="p-3 border text-sm text-gray-600">
                          {yard.min_metric.toFixed(2)} - {yard.max_metric.toFixed(2)}
                        </td>
                        <td className="p-3 border text-center">{yard.device_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Carrier Trends Tab */}
      {activeTab === 'carrier-trends' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">Carrier Performance Trends</h2>
          <p className="text-gray-600 mb-6">Track how carrier performance changes over time. Leave yards empty for all yards, or add one or more yards to see side-by-side charts per yard.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric</label>
              <select
                value={carrierTrends.metric}
                onChange={(e) => setCarrierTrends({ ...carrierTrends, metric: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="upload">Upload Speed</option>
                <option value="download">Download Speed</option>
                <option value="ping">Ping/Latency</option>
                <option value="signal">Signal Strength</option>
                <option value="battery">Battery</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <select
                value={carrierTrends.timeRange}
                onChange={(e) => setCarrierTrends({ ...carrierTrends, timeRange: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="7 days">Last 7 Days</option>
                <option value="30 days">Last 30 Days</option>
                <option value="90 days">Last 90 Days</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Interval</label>
              <select
                value={carrierTrends.interval}
                onChange={(e) => setCarrierTrends({ ...carrierTrends, interval: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="hour">Hourly</option>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
              </select>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Yard(s) — optional</label>
            <p className="text-xs text-gray-500 mb-2">Empty = all yards in one chart. Add one or more yards to see one chart per yard side by side.</p>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                type="text"
                value={carrierYardInput}
                onChange={(e) => setCarrierYardInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCarrierTrendYard())}
                placeholder="e.g. 43"
                className="w-24 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={addCarrierTrendYard}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                Add yard
              </button>
              {(carrierTrends.yard_numbers || []).map((yard) => (
                <span
                  key={yard}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                >
                  Yard {yard}
                  <button
                    type="button"
                    onClick={() => removeCarrierTrendYard(yard)}
                    className="hover:opacity-70 font-bold"
                    aria-label={`Remove yard ${yard}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <button
            onClick={handleCarrierTrends}
            disabled={carrierLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {carrierLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Loading Trends...</span>
              </>
            ) : (
              <>
                <FaChartLine />
                <span>Get Trends</span>
              </>
            )}
          </button>
          
          {/* Results — Option B: one graph per yard; each yard has checkboxes to pick which carriers to show on that graph */}
          {carrierResult && carrierResult.length > 0 && (
            <div className="mt-6 space-y-6">
              <div className={`grid gap-6 ${carrierResult.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                {carrierResult.map((yardBlock) => {
                  const allCarrierNames = (yardBlock.carriers || []).map((c) => c.carrier);
                  const effectiveCarriers = getEffectiveCarriersForYard(yardBlock.yard_number, allCarrierNames);
                  const { data: chartData, carrierNames } = formatGraphDataForYardBlock(
                    yardBlock,
                    effectiveCarriers.length > 0 ? effectiveCarriers : null
                  );
                  const isAllYards = yardBlock.yard_number === 'all';
                  const title = isAllYards ? 'All yards' : `Yard ${yardBlock.yard_number}`;
                  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff00ff', '#00ffff'];
                  const noneSelected = effectiveCarriers.length === 0 && allCarrierNames.length > 0;
                  const allSelected = effectiveCarriers.length === allCarrierNames.length && allCarrierNames.length > 0;
                  return (
                    <div key={yardBlock.yard_number} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="font-semibold text-lg text-gray-800 mb-3">{title}</h3>
                      {/* Per-yard carrier checkboxes: choose which carriers to draw on this yard's graph */}
                      {allCarrierNames.length > 0 && (
                        <div className="mb-3 p-2 bg-white rounded border border-gray-200">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-700">Show on graph:</span>
                            <button
                              type="button"
                              onClick={() => setAllCarriersForYard(yardBlock.yard_number, true)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              All
                            </button>
                            <span className="text-gray-400">|</span>
                            <button
                              type="button"
                              onClick={() => setAllCarriersForYard(yardBlock.yard_number, false)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              None
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {yardBlock.carriers.map((c) => {
                              const checked = effectiveCarriers.includes(c.carrier);
                              return (
                                <label key={c.carrier} className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCarrierForYard(yardBlock.yard_number, c.carrier)}
                                    className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 rounded"
                                  />
                                  <span className="text-sm text-gray-700">{c.carrier}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {noneSelected && allCarrierNames.length > 0 ? (
                        <p className="text-sm text-amber-600 py-4">Select at least one carrier above to show on the graph.</p>
                      ) : chartData.length > 0 && carrierNames.length > 0 ? (
                        <ResponsiveContainer width="100%" height={420}>
                          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 70 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="time"
                              angle={-45}
                              textAnchor="end"
                              height={70}
                              interval="preserveStartEnd"
                              minTickGap={24}
                              tick={{ fontSize: 10 }}
                            />
                            <YAxis
                              label={{ value: getMetricLabel(carrierResultMetric) + ` (${getMetricUnit(carrierResultMetric)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                              tick={{ fontSize: 11 }}
                            />
                            <Tooltip />
                            {/* Legend on top so carrier names aren't clipped against the rotated x-axis labels */}
                            <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: 8 }} />
                            {carrierNames.map((carrierName, idx) => (
                              <Line
                                key={carrierName}
                                type="monotone"
                                dataKey={carrierName}
                                stroke={colors[idx % colors.length]}
                                strokeWidth={2}
                                name={carrierName}
                                dot={chartData.length > 30 ? false : { r: 2 }}
                                connectNulls={false}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-gray-500 py-4">No trend data for this yard.</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Graph Modal (combined or individual) */}
              {(graphView === 'combined' || graphView === 'individual') && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setGraphView(null); setGraphCarrier(null); setGraphYardNumber(null); }}>
                  <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800">
                          {graphView === 'combined'
                            ? `Combined: ${getMetricLabel(carrierResultMetric)}${graphYardNumber && graphYardNumber !== 'all' ? ` — Yard ${graphYardNumber}` : ''}`
                            : `${graphCarrier} - ${getMetricLabel(carrierResultMetric)}`
                          }
                        </h3>
                        {graphView === 'combined' && selectedCarriers.length > 0 && (
                          <p className="text-sm text-gray-600 mt-1">Carriers: {selectedCarriers.join(', ')}</p>
                        )}
                      </div>
                      <button type="button" onClick={() => { setGraphView(null); setGraphCarrier(null); setGraphYardNumber(null); }} className="p-2 hover:bg-gray-100 rounded-full" title="Close">
                        <FaTimes className="text-gray-600" size={20} />
                      </button>
                    </div>
                    <div className="p-6">
                      {graphView === 'combined' && selectedCarriers.length > 0 && (() => {
                        const yardBlock = graphYardNumber != null ? carrierResult.find((y) => y.yard_number === graphYardNumber) : carrierResult[0];
                        const graphData = yardBlock ? formatCombinedGraphData(yardBlock) : [];
                        return graphData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={500}>
                            <LineChart data={graphData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="time" angle={-45} textAnchor="end" height={100} interval="preserveStartEnd" tick={{ fontSize: 11 }} />
                              <YAxis label={{ value: getMetricLabel(carrierResultMetric) + ` (${getMetricUnit(carrierResultMetric)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '20px' }} />
                              {selectedCarriers.map((carrierName, idx) => {
                                const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff00ff', '#00ffff'];
                                return (
                                  <Line key={carrierName} type="monotone" dataKey={carrierName} stroke={colors[idx % colors.length]} strokeWidth={2} name={carrierName} dot={{ r: 3 }} connectNulls={false} />
                                );
                              })}
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="p-8 text-center text-gray-500">No data for selected carriers.</div>
                        );
                      })()}
                      {graphView === 'individual' && graphCarrier && (() => {
                        let carrierData = null;
                        for (const y of carrierResult || []) {
                          const c = y.carriers && y.carriers.find((cr) => cr.carrier === graphCarrier);
                          if (c) {
                            carrierData = c;
                            break;
                          }
                        }
                        return carrierData ? (
                          <ResponsiveContainer width="100%" height={500}>
                            <LineChart data={formatIndividualGraphData(carrierData)} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="time" angle={-45} textAnchor="end" height={100} interval="preserveStartEnd" tick={{ fontSize: 11 }} />
                              <YAxis label={{ value: getMetricLabel(carrierResultMetric) + ` (${getMetricUnit(carrierResultMetric)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Legend verticalAlign="top" height={36} />
                              <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name={getMetricLabel(carrierResultMetric) + ` (${getMetricUnit(carrierResultMetric)})`} dot={{ r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

